import React from "react";
import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionLabel, StatsBand, ClosingCTA } from "../components/Sections";
import { IMAGES } from "../data";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        label="About Us"
        title="Wealth management for your rental property."
        intro="Kept exists to give time-poor property owners their freedom back, delivering premium management that protects, grows, and honours the value of their investments without demanding their attention."
        image={IMAGES.marble}
      />

      {/* Mission */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="mission-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4"><SectionLabel>Our mission</SectionLabel></div>
          <Reveal className="md:col-span-8">
            <p className="font-serif text-2xl md:text-4xl font-light leading-tight text-kept-ink">
              We exist to give time-poor property owners their freedom back, delivering premium
              management that protects, grows, and honours the value of their investments without
              demanding their attention.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What we're building */}
      <section className="bg-kept-ink grain" data-testid="building-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="kept-label text-kept-brass mb-6">What we're building</div>
          </div>
          <Reveal className="md:col-span-8">
            <p className="text-kept-sand text-xl md:text-2xl font-light leading-relaxed">
              Comprehensive, end-to-end management for high-end residential and small commercial
              properties across New Zealand. From tenant placement and lease administration to
              Healthy Homes compliance and ongoing maintenance, we operate as a single, accountable
              point of stewardship for your asset.
            </p>
            <p className="text-kept-sand/60 text-base mt-8 leading-relaxed max-w-2xl">
              The market is fragmented, spanning large franchises and boutique operators. Kept is
              built for owners who expect the discipline of financial services applied to the care
              of a home.
            </p>
          </Reveal>
        </div>
      </section>

      <StatsBand />

      {/* Team / credibility */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="team-section">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={IMAGES.professional} alt="Kept property management team" className="w-full h-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-kept-ink/10" />
            </div>
          </motion.div>
          <div className="lg:col-span-7">
            <SectionLabel>The people behind Kept</SectionLabel>
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-kept-ink leading-tight">
                A financial-services mindset, applied to property.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">
                Our team brings backgrounds in financial services and business development to every
                property we manage. That means rigorous
                budgeting, transparent costing, and analytical reporting, the difference between
                managing a property by instinct and managing it by evidence.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-muted-foreground mt-4 text-base md:text-lg leading-relaxed">
                It is the main reason owners choose Kept over generic managers in a crowded, fragmented
                market: your investment is treated with the same discipline a portfolio deserves.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Vision, aspirational close */}
      <section className="bg-kept-stone" data-testid="vision-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <div className="max-w-xl mx-auto md:mx-0"><SectionLabel>Where we're headed</SectionLabel></div>
          <Reveal>
            <p className="font-serif text-3xl md:text-5xl font-light leading-[1.15] text-kept-ink max-w-5xl">
              A New Zealand where owning investment property feels effortless, where high-value
              assets are cared for as if they were the carer's own, where homes exceed compliance
              standards rather than just meet them, and where premium property management is the
              norm every investor deserves, not a rare exception.
            </p>
          </Reveal>
        </div>
      </section>

      <ClosingCTA
        heading="Let's protect your investment."
        sub="Discover what premium, financially disciplined management looks like, designed around your goals."
      />
    </div>
  );
}
